import { useState, FormEvent } from 'react';

interface FooterProps {
  onNavigate: (section: string) => void;
}

export default function Footer({ onNavigate }: FooterProps) {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleNewsletterSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('email', email);

      const response = await fetch('https://readdy.ai/api/form/d58oocvahj0hkf71i7ag', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setEmail('');
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <footer className="bg-[#1A2332] text-white">
      {/* CTA Section */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-[#FF6B6B] to-[#1A2332]"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 py-20 text-center">
          <h2 className="font-['Playfair_Display'] text-5xl font-bold mb-4">
            Ready to Elevate Your Court Reporting Practice?
          </h2>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            Join 200+ professionals who trust us with their online presence
          </p>
          <button
            onClick={() => onNavigate('contact')}
            className="bg-white text-[#1A2332] px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer shadow-lg"
          >
            Schedule Free Consultation
            <i className="ri-calendar-line ml-2"></i>
          </button>
          <p className="text-sm text-white/80 mt-4">Or call us at 1-516-554-1040</p>
        </div>
      </div>

      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-12">
          {/* Brand Column */}
          <div className="lg:col-span-2">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-gradient-to-br from-[#FF6B6B] to-[#FF8E8E] rounded-lg flex items-center justify-center">
                <i className="ri-quill-pen-line text-xl text-white"></i>
              </div>
              <div>
                <div className="font-['Playfair_Display'] text-xl font-bold leading-tight text-white">
                  Court Reporters
                </div>
                <div className="text-xs font-medium tracking-wide text-[#FF6B6B]">
                  WEBSITES
                </div>
              </div>
            </div>
            <p className="font-['Playfair_Display'] text-xl text-gray-300 mb-6 leading-relaxed">
              Building Digital Presence for Legal Professionals
            </p>
            
            {/* Newsletter */}
            <form id="newsletter-form" onSubmit={handleNewsletterSubmit} data-readdy-form className="mb-6">
              <div className="flex gap-2">
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Your email address"
                  required
                  className="flex-1 px-4 py-3 rounded-lg bg-transparent border border-white/20 text-white placeholder-gray-400 focus:border-[#FF6B6B] focus:ring-2 focus:ring-[#FF6B6B]/20 outline-none transition-all text-sm"
                />
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="bg-[#FF6B6B] text-white w-12 h-12 rounded-lg flex items-center justify-center hover:bg-[#ff5252] transition-colors disabled:opacity-50 cursor-pointer flex-shrink-0"
                >
                  <i className={`${isSubmitting ? 'ri-loader-4-line animate-spin' : 'ri-mail-send-line'}`}></i>
                </button>
              </div>
              {submitStatus === 'success' && (
                <p className="text-green-400 text-xs mt-2">Thanks for subscribing!</p>
              )}
              {submitStatus === 'error' && (
                <p className="text-red-400 text-xs mt-2">Something went wrong. Please try again.</p>
              )}
            </form>
          </div>

          {/* Services Column */}
          <div>
            <h4 className="text-[#FF6B6B] font-semibold mb-4">Services</h4>
            <ul className="space-y-3">
              <li>
                <button
                  onClick={() => onNavigate('services')}
                  className="text-gray-300 hover:text-[#FF6B6B] transition-colors text-sm cursor-pointer"
                >
                  Website Design
                </button>
              </li>
              <li>
                <button
                  onClick={() => onNavigate('pricing')}
                  className="text-gray-300 hover:text-[#FF6B6B] transition-colors text-sm cursor-pointer"
                >
                  Pricing Plans
                </button>
              </li>
              <li>
                <button
                  onClick={() => onNavigate('seo')}
                  className="text-gray-300 hover:text-[#FF6B6B] transition-colors text-sm cursor-pointer"
                >
                  SEO Optimization
                </button>
              </li>
              <li>
                <button
                  onClick={() => onNavigate('support')}
                  className="text-gray-300 hover:text-[#FF6B6B] transition-colors text-sm cursor-pointer"
                >
                  Maintenance Plans
                </button>
              </li>
              <li>
                <button
                  onClick={() => onNavigate('services')}
                  className="text-gray-300 hover:text-[#FF6B6B] transition-colors text-sm cursor-pointer"
                >
                  Content Writing
                </button>
              </li>
            </ul>
          </div>

          {/* Company Column */}
          <div>
            <h4 className="text-[#FF6B6B] font-semibold mb-4">Company</h4>
            <ul className="space-y-3">
              <li>
                <button
                  onClick={() => onNavigate('home')}
                  className="text-gray-300 hover:text-[#FF6B6B] transition-colors text-sm cursor-pointer"
                >
                  About Us
                </button>
              </li>
              <li>
                <button
                  onClick={() => onNavigate('portfolio')}
                  className="text-gray-300 hover:text-[#FF6B6B] transition-colors text-sm cursor-pointer"
                >
                  Portfolio
                </button>
              </li>
              <li>
                <button
                  onClick={() => onNavigate('portfolio')}
                  className="text-gray-300 hover:text-[#FF6B6B] transition-colors text-sm cursor-pointer"
                >
                  Testimonials
                </button>
              </li>
              <li>
                <a
                  href="#"
                  className="text-gray-300 hover:text-[#FF6B6B] transition-colors text-sm cursor-pointer"
                >
                  Blog
                </a>
              </li>
            </ul>
          </div>

          {/* Support Column */}
          <div>
            <h4 className="text-[#FF6B6B] font-semibold mb-4">Support</h4>
            <ul className="space-y-3">
              <li>
                <button
                  onClick={() => onNavigate('contact')}
                  className="text-gray-300 hover:text-[#FF6B6B] transition-colors text-sm cursor-pointer"
                >
                  Contact
                </button>
              </li>
              <li>
                <a
                  href="#"
                  className="text-gray-300 hover:text-[#FF6B6B] transition-colors text-sm cursor-pointer"
                >
                  FAQs
                </a>
              </li>
              <li>
                <a
                  href="#"
                  className="text-gray-300 hover:text-[#FF6B6B] transition-colors text-sm cursor-pointer"
                >
                  Documentation
                </a>
              </li>
              <li>
                <a
                  href="#"
                  className="text-gray-300 hover:text-[#FF6B6B] transition-colors text-sm cursor-pointer"
                >
                  Training
                </a>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="text-white font-semibold mb-4">Quick Links</h4>
            <ul className="space-y-2">
              <li>
                <a href="#services" className="text-gray-300 hover:text-white transition-colors cursor-pointer">
                  Services
                </a>
              </li>
              <li>
                <a href="#portfolio" className="text-gray-300 hover:text-white transition-colors cursor-pointer">
                  Portfolio
                </a>
              </li>
              <li>
                <a href="#pricing" className="text-gray-300 hover:text-white transition-colors cursor-pointer">
                  Pricing
                </a>
              </li>
              <li>
                <a href="/flyer" className="text-gray-300 hover:text-white transition-colors cursor-pointer">
                  Special Offer
                </a>
              </li>
              <li>
                <a href="#contact" className="text-gray-300 hover:text-white transition-colors cursor-pointer">
                  Contact
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-sm text-gray-400">
              © 2025 Court Reporters Websites. All rights reserved.
            </p>

            {/* Social Media */}
            <div className="flex items-center gap-4">
              <a
                href="#"
                className="w-10 h-10 border-2 border-white/20 rounded-lg flex items-center justify-center text-white hover:bg-[#FF6B6B] hover:border-[#FF6B6B] transition-all cursor-pointer"
                aria-label="LinkedIn"
              >
                <i className="ri-linkedin-fill"></i>
              </a>
              <a
                href="#"
                className="w-10 h-10 border-2 border-white/20 rounded-lg flex items-center justify-center text-white hover:bg-[#FF6B6B] hover:border-[#FF6B6B] transition-all cursor-pointer"
                aria-label="Facebook"
              >
                <i className="ri-facebook-fill"></i>
              </a>
              <a
                href="#"
                className="w-10 h-10 border-2 border-white/20 rounded-lg flex items-center justify-center text-white hover:bg-[#FF6B6B] hover:border-[#FF6B6B] transition-all cursor-pointer"
                aria-label="Twitter"
              >
                <i className="ri-twitter-x-fill"></i>
              </a>
              <a
                href="#"
                className="w-10 h-10 border-2 border-white/20 rounded-lg flex items-center justify-center text-white hover:bg-[#FF6B6B] hover:border-[#FF6B6B] transition-all cursor-pointer"
                aria-label="Instagram"
              >
                <i className="ri-instagram-fill"></i>
              </a>
            </div>

            <div className="flex items-center gap-4 text-sm text-gray-400">
              <a href="#" className="hover:text-[#FF6B6B] transition-colors cursor-pointer">
                Privacy Policy
              </a>
              <span>|</span>
              <a href="#" className="hover:text-[#FF6B6B] transition-colors cursor-pointer">
                Terms of Service
              </a>
              <span>|</span>
              <a href="https://readdy.ai/?ref=logo" className="hover:text-[#FF6B6B] transition-colors cursor-pointer">
                Powered by Readdy
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
